/**
 * Planner.jsx
 * Study planner with an in-course calendar. The student picks a start date,
 * hours per week and study days; the plan spreads the course hours across the
 * modules in order and shows the finish date. The choice is saved with the
 * student's course record. An optional calendar file can be downloaded.
 * Reminders by email will be added later.
 */
import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../Libs/firebase-config.mjs";
import { MODULES, PARTS, TOTAL_HOURS } from "./courseMap";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const fmt = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

/** Spread the course hours over the chosen study days, module by module. */
export function buildPlan(startStr, hoursPerWeek, days) {
  const perDay = Math.max(0.5, hoursPerWeek / Math.max(1, days.length));
  const byDay = {}; const modEnd = {};
  let i = 0, left = MODULES[0].hours, d = parse(startStr), last = d, guard = 0;
  while (i < MODULES.length && guard++ < 3000) {
    if (days.includes(d.getDay())) {
      let cap = perDay;
      while (cap > 0.001 && i < MODULES.length) {
        const use = Math.min(cap, left);
        (byDay[key(d)] = byDay[key(d)] || []).push({ no: MODULES[i].no, hours: use });
        cap -= use; left -= use; last = new Date(d);
        if (left <= 0.001) { modEnd[MODULES[i].no] = key(d); i += 1; left = i < MODULES.length ? MODULES[i].hours : 0; }
      }
    }
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  }
  const partEnd = {};
  PARTS.forEach((p) => { const lastMod = MODULES.filter((m) => m.part === p.no).slice(-1)[0]; partEnd[modEnd[lastMod.no]] = p.no; });
  return { byDay, modEnd, partEnd, finish: last, weeks: Math.ceil(TOTAL_HOURS / hoursPerWeek) };
}

export default function Planner({ isDark, user }) {
  const ink = isDark ? "#f0f7f0" : "#0d1a0f";
  const sub = isDark ? "#9fb7a2" : "#4b6a50";
  const card = isDark ? "#13231a" : "#ffffff";
  const line = isDark ? "#223328" : "#d8edd8";
  const soft = isDark ? "#16301c" : "#eef6ea";
  const [start, setStart] = useState(key(new Date()));
  const [hpw, setHpw] = useState(5);
  const [days, setDays] = useState([1, 3, 6]);
  const [month, setMonth] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const [picked, setPicked] = useState(null);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    if (!user?.token) return;
    getDoc(doc(db, "imfuyo_academy", user.token, "mbuziplus_course", user.pid || user.token)).then((s) => {
      const p = s.exists() && s.data().planner;
      if (p) { setStart(p.start); setHpw(p.hpw); setDays(p.days); setMonth(parse(p.start)); }
    }).catch(() => {});
  }, [user?.token]);

  const plan = useMemo(() => buildPlan(start, hpw, days.length ? days : [1]), [start, hpw, days]);

  const save = async () => {
    if (!user?.token) return;
    try {
      await setDoc(doc(db, "imfuyo_academy", user.token, "mbuziplus_course", user.pid || user.token), { planner: { start, hpw, days }, updatedAt: serverTimestamp() }, { merge: true });
      setSaved("Plan saved.");
    } catch { setSaved("Could not save your plan. Please try again."); }
  };

  const ics = () => {
    const stamp = (s) => s.replace(/-/g, "");
    const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Imfuyo Academy//MbuziPlus Course//EN"];
    Object.entries(plan.byDay).forEach(([k, items]) => {
      const next = key(new Date(parse(k).getTime() + 86400000));
      const title = items.map((x) => `Module ${x.no}`).join(", ");
      lines.push("BEGIN:VEVENT", `UID:mbz-${k}@imfuyo.farm`, `DTSTAMP:${stamp(key(new Date()))}T000000Z`, `DTSTART;VALUE=DATE:${stamp(k)}`, `DTEND;VALUE=DATE:${stamp(next)}`,
        `SUMMARY:MbuziPlus Course study: ${title}`, `DESCRIPTION:${items.map((x) => `Module ${x.no} ${MODULES[x.no - 1].title} (${x.hours.toFixed(1)} hours)`).join("\\n")}`, "END:VEVENT");
    });
    lines.push("END:VCALENDAR");
    const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/calendar" }));
    const a = document.createElement("a"); a.href = url; a.download = "MbuziPlus-Study-Plan.ics"; a.click(); URL.revokeObjectURL(url);
  };

  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const cells = [];
  for (let i = 0; i < first.getDay(); i++) cells.push(null);
  for (let d = 1; d <= new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate(); d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  const today = key(new Date());
  const field = { fontFamily: "inherit", fontSize: 14, padding: "8px 10px", borderRadius: 10, border: `1px solid ${line}`, background: isDark ? "#0d1a0f" : "#fff", color: ink };
  const pickedItems = picked ? plan.byDay[picked] || [] : [];

  return (
    <section aria-label="Study planner" style={{ marginBottom: 36 }}>
      <h2 style={{ margin: "0 0 4px", fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 24, color: ink }}>Plan your study time</h2>
      <p style={{ margin: "0 0 14px", fontSize: 13.5, color: sub }}>Choose when you will study. We spread the {TOTAL_HOURS} course hours over your days, in order, and show your finish date.</p>
      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: 16, padding: 18 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          <label style={{ fontSize: 13, color: sub, display: "grid", gap: 4 }}>Start date
            <input type="date" value={start} onChange={(e) => e.target.value && setStart(e.target.value)} style={field} />
          </label>
          <label style={{ fontSize: 13, color: sub, display: "grid", gap: 4 }}>Hours each week
            <select value={hpw} onChange={(e) => setHpw(Number(e.target.value))} style={field}>
              {[2, 3, 4, 5, 6, 8, 10, 12, 15, 20].map((h) => <option key={h} value={h}>{h} hours</option>)}
            </select>
          </label>
          <div style={{ fontSize: 13, color: sub }}>
            <div style={{ marginBottom: 4 }}>Study days</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {DAYS.map((n, i) => {
                const on = days.includes(i);
                return <button key={n} onClick={() => setDays(on ? days.filter((x) => x !== i) : [...days, i].sort())} aria-pressed={on}
                  style={{ ...field, padding: "7px 11px", cursor: "pointer", fontWeight: 700, background: on ? "#3E7A32" : field.background, color: on ? "#fff" : ink, borderColor: on ? "#3E7A32" : line }}>{n}</button>;
              })}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16, background: soft, borderRadius: 12, padding: "12px 14px", fontSize: 14, color: ink, lineHeight: 1.6 }}>
          At <b>{hpw} hours a week</b> on {days.length || 1} day{days.length === 1 ? "" : "s"}, you finish the whole course on <b>{fmt(plan.finish)}</b>, about {plan.weeks} weeks from your start.
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "18px 0 8px" }}>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} style={{ ...field, cursor: "pointer" }} aria-label="Previous month">‹</button>
          <b style={{ color: ink }}>{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</b>
          <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} style={{ ...field, cursor: "pointer" }} aria-label="Next month">›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))", gap: 4 }}>
          {DAYS.map((n) => <div key={n} style={{ textAlign: "center", fontSize: 11.5, fontWeight: 700, color: sub }}>{n}</div>)}
          {cells.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const k = key(d); const items = plan.byDay[k]; const part = plan.partEnd[k]; const sel = picked === k;
            return (
              <button key={k} onClick={() => setPicked(sel ? null : k)} aria-label={k}
                style={{ minHeight: 56, borderRadius: 10, border: `1.5px solid ${sel ? "#285020" : k === today ? "#D9601A" : line}`, background: items ? (isDark ? "#1b3a22" : "#e3f1dc") : "transparent", color: ink, padding: 4, cursor: items ? "pointer" : "default", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{d.getDate()}</span>
                {items && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#285020", ...(isDark ? { color: "#b7d9a8" } : {}) }}>{items.map((x) => `M${x.no}`).join(" ")}</span>}
                {part && <span style={{ fontSize: 10, fontWeight: 800, color: "#D9601A" }}>Part {part} done</span>}
              </button>
            );
          })}
        </div>
        {picked && (
          <div style={{ marginTop: 12, background: soft, borderRadius: 12, padding: "10px 14px", fontSize: 13.5, color: ink }}>
            <b>{fmt(parse(picked))}</b>
            {pickedItems.length ? pickedItems.map((x) => <div key={x.no}>Module {x.no}: {MODULES[x.no - 1].title} ({x.hours.toFixed(1)} hours){plan.modEnd[x.no] === picked ? " . Finish today" : ""}</div>) : <div>No study planned.</div>}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16, alignItems: "center" }}>
          <button onClick={save} style={{ border: "none", background: "#285020", color: "#fff", fontWeight: 700, fontSize: 14, borderRadius: 999, padding: "10px 20px", cursor: "pointer", fontFamily: "inherit" }}>Save my plan</button>
          <button onClick={ics} style={{ border: `1.5px solid ${line}`, background: "transparent", color: ink, fontWeight: 700, fontSize: 14, borderRadius: 999, padding: "10px 20px", cursor: "pointer", fontFamily: "inherit" }}>Add to my phone calendar</button>
          {saved && <span role="status" style={{ fontSize: 13, color: sub }}>{saved}</span>}
        </div>
        <p style={{ margin: "10px 0 0", fontSize: 12, color: sub }}>Reminders by email are coming soon. The plan does not change your progress; you finish a module by reading its lessons and passing its quiz.</p>
      </div>
    </section>
  );
}
